import { useState, useEffect } from "react";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";

export type SubscriptionStatus = "Active" | "Inactive" | "Trial";

export const useSubscription = (orgId: string | null) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!orgId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Query subscriptions collection by orgId
        const subscriptionsRef = collection(db, "subscriptions");
        const q = query(subscriptionsRef, where("orgId", "==", orgId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const subscriptionData = querySnapshot.docs[0].data();
          const status = subscriptionData.status as SubscriptionStatus;
          setSubscriptionStatus(status || "Inactive");
        } else {
          // If no subscription found, check organization document for backward compatibility
          const orgRef = doc(db, "organization", orgId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            const status = orgData.status as SubscriptionStatus;
            setSubscriptionStatus(status || "Inactive");
          } else {
            setSubscriptionStatus("Inactive");
          }
        }
      } catch (err: any) {
        console.error("Error fetching subscription status:", err);
        setError(err?.message || "Failed to fetch subscription status");
        setSubscriptionStatus("Inactive");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, [orgId]);

  const hasAccess = subscriptionStatus === "Active" || subscriptionStatus === "Trial";

  return {
    subscriptionStatus,
    hasAccess,
    loading,
    error,
  };
};

